import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { paymentsApi, checksApi, ordersApi } from '../services/api';
import { useOrderStore, mapServerOrderItems } from '../stores/orderStore';
import { offlineService, useOnlineStatus } from '../services/offline';
import { useToast } from '../components/Toast';
import SplitCheckModal from '../components/SplitCheckModal';
import DiscountModal from '../components/DiscountModal';
import { CardPaymentModal } from '../components/CardPaymentModal';
import { isTapToPaySupported, takeTapToPayPayment, cancelTapToPay, TapToPayStatus } from '../services/tapToPay';
import ContactlessIcon from '../components/ContactlessIcon';
import {
  ArrowLeft,
  CreditCard,
  Banknote,
  Smartphone,
  Gift,
  Check,
  Calculator,
  Percent,
  Users,
  Printer,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { printerService } from '../services/printer';
import { useAuthStore } from '../stores/authStore';
import clsx from 'clsx';

type PaymentMethod = 'card' | 'cash' | 'apple_pay' | 'google_pay' | 'gift_card';

interface Discount {
  type: 'percent' | 'fixed' | 'promo';
  value: number;
  code?: string;
  reason?: string;
}

interface SplitConfig {
  type: 'even' | 'by_seat' | 'by_item' | 'custom';
  splits: number;
  amounts?: number[];
  splitIds?: string[]; // IDs from backend check_splits table
  currentSplit: number;
  paidSplits: number[];
}

const tipPresets = [15, 18, 20, 25];

export default function CheckoutPage() {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const { checkId, items, partySize, getSubtotal, clearOrder, tableNumber, guestName, taxRate, loadItems } = useOrderStore();
  const { isOnline } = useOnlineStatus();
  const { employee, restaurant } = useAuthStore();
  const { showError, showSuccess } = useToast();

  // The server's check is the source of truth for what's on the tab. Always
  // fetch it — items may have been fired/voided from the Courses view (or
  // another device) after the local cart was built, and the payment amount is
  // validated server-side against the check, so charging from a stale local
  // cart shows (and charges) the wrong total.
  const { data: checkData } = useQuery({
    queryKey: ['check', checkId],
    queryFn: async () => {
      if (!checkId) return null;
      const response = await checksApi.getOne(checkId);
      return response.data;
    },
    enabled: !!checkId,
  });

  // Replace the local cart with the server's items whenever fresh check data
  // arrives — unless there are unsent local items (holdUntilFired undefined
  // means never sent), which the server doesn't know about yet.
  useEffect(() => {
    const hasUnsentLocalItems = items.some((i: any) => i.holdUntilFired === undefined);
    if (checkData?.orders && !hasUnsentLocalItems) {
      const allItems = mapServerOrderItems(checkData.orders);
      if (allItems.length > 0) {
        loadItems(allItems);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkData, loadItems]);

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  const [tipPercent, setTipPercent] = useState<number | null>(18);
  const [customTip, setCustomTip] = useState('');
  const [cashReceived, setCashReceived] = useState('');
  const [success, setSuccess] = useState(false);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  // Split check state
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitConfig, setSplitConfig] = useState<SplitConfig | null>(null);

  // Discount state — applied on the SERVER (client-only discounts left the
  // check underpaid forever); local shape kept for the pre-check display
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discount, setDiscount] = useState<Discount | null>(null);
  const [discountPinPrompt, setDiscountPinPrompt] = useState<Discount | null>(null);
  const [discountPin, setDiscountPin] = useState('');

  const applyDiscount = async (newDiscount: Discount, managerPin?: string) => {
    if (!checkId) {
      showError('Send the order to the kitchen first, then apply the discount.');
      return;
    }
    // The server ADDS discounts together — applying twice from here would
    // stack silently. One discount per check from the POS.
    if ((checkData?.discountTotalCents || 0) > 0) {
      showError('A discount is already on this check. A manager can adjust it from the dashboard.');
      return;
    }
    const response = await checksApi.applyDiscount(checkId, {
      discountType: newDiscount.type === 'percent' ? 'percentage' : 'fixed_amount',
      value: newDiscount.value,
      reason: newDiscount.reason || newDiscount.code,
      ...(managerPin ? { managerPin } : {}),
    } as any);
    if (response.success) {
      setDiscount(newDiscount);
      setDiscountPinPrompt(null);
      setDiscountPin('');
      queryClient.invalidateQueries({ queryKey: ['check', checkId] });
      showSuccess('Discount applied.');
    } else if (response.error?.code === 'MANAGER_AUTH_REQUIRED') {
      setDiscountPinPrompt(newDiscount);
    } else {
      setDiscountPinPrompt(null);
      setDiscountPin('');
      showError(response.error?.message || "Couldn't apply the discount.");
    }
  };

  // Card payment modal state
  const [showCardPaymentModal, setShowCardPaymentModal] = useState(false);

  // Tap to Pay progress (null = idle)
  const [tapStatus, setTapStatus] = useState<TapToPayStatus | null>(null);
  // Bumped on cancel so a hung/late Tap to Pay run can't act after dismissal
  const tapRunIdRef = useRef(0);

  // The server check is the source of truth once everything is sent: every
  // displayed line (subtotal, discount, tax) comes from IT, so the lines
  // always sum to exactly what gets charged. Local math only covers the
  // moments before items reach the server.
  const hasUnsentItems = items.some((i: any) => i.holdUntilFired === undefined);
  const serverLive = !hasUnsentItems && checkData?.amountDueCents != null;

  const localSubtotal = getSubtotal();
  const localDiscountAmount = discount
    ? discount.type === 'percent'
      ? Math.round(localSubtotal * (discount.value / 100))
      : discount.value
    : 0;

  const subtotal = serverLive ? checkData.subtotalCents : localSubtotal;
  const discountAmount = serverLive ? (checkData.discountTotalCents || 0) : localDiscountAmount;
  const discountedSubtotal = subtotal - discountAmount;
  const tax = serverLive ? (checkData.taxTotalCents || 0) : Math.round(discountedSubtotal * taxRate);

  const tipAmount = customTip
    ? Math.round(parseFloat(customTip) * 100)
    : tipPercent
    ? Math.round(discountedSubtotal * (tipPercent / 100))
    : 0;

  const fullTotal = serverLive
    ? checkData.amountDueCents + tipAmount
    : discountedSubtotal + tax + tipAmount;

  const getCurrentSplitAmount = () => {
    if (!splitConfig || !splitConfig.amounts) return fullTotal;
    return splitConfig.amounts[splitConfig.currentSplit] || 0;
  };

  const total = splitConfig ? getCurrentSplitAmount() : fullTotal;

  const queryClient = useQueryClient();

  // Anything still only in the local cart must land on the server check
  // BEFORE money moves — paying a check that never got its items records a
  // wrong (even $0) payment. Returns the fresh server check.
  const ensureItemsOnCheck = async (currentCheckId: string) => {
    const unsent = items.filter((i: any) => i.holdUntilFired === undefined);
    if (unsent.length > 0) {
      const resp = await ordersApi.create({
        checkId: currentCheckId,
        items: unsent.map((item: any) => ({
          menuItemId: item.menuItemId,
          quantity: item.quantity,
          seatNumber: item.seatNumber,
          courseNumber: item.courseNumber,
          modifiers: item.modifiers,
          specialInstructions: item.specialInstructions,
        })),
        fireImmediately: true,
      });
      if (!resp.success) {
        throw new Error(resp.error?.message || "Couldn't send the remaining items to the kitchen — payment not started.");
      }
    }
    const fresh = await checksApi.getOne(currentCheckId);
    queryClient.invalidateQueries({ queryKey: ['check', currentCheckId] });
    if (!fresh.success || !fresh.data) {
      throw new Error("Couldn't refresh the check — payment not started.");
    }
    return fresh.data as any;
  };

  const processPaymentMutation = useMutation({
    mutationFn: async () => {
      const splitTipAmount = splitConfig ? Math.round(tipAmount / splitConfig.splits) : tipAmount;

      if (!isOnline) {
        // A card that isn't present later can't be charged later — never
        // pretend an offline card tap went through.
        if (paymentMethod !== 'cash') {
          throw new Error(
            'Card payments need a connection — it usually comes back in a moment. Cash works offline and syncs automatically.'
          );
        }
        const currentCheckId = checkId || `offline-check-${tableId}`;
        const offlinePaymentId = `offline-payment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        await offlineService.queuePayment({
          id: offlinePaymentId,
          checkId: currentCheckId,
          tableId: tableId || undefined,
          splitCheckId: splitConfig?.splitIds?.[splitConfig.currentSplit],
          method: paymentMethod,
          amountCents: total,
          tipCents: splitTipAmount,
          amountTenderedCents: cashReceived ? Math.round(parseFloat(cashReceived) * 100) : total,
          createdAt: new Date().toISOString(),
        });
        return { id: offlinePaymentId, offline: true };
      }

      // Create check if it doesn't exist
      let currentCheckId = checkId;
      if (!currentCheckId && tableId) {
        const checkResponse = await checksApi.open({
          tableId: tableId,
          partySize: partySize || 1,
        });
        if (checkResponse.success && checkResponse.data?.id) {
          currentCheckId = checkResponse.data.id;
          // Update the store so CardPaymentModal can use it
          useOrderStore.getState().setCheckId(currentCheckId);
        }
      }

      if (!currentCheckId) throw new Error('Could not create check');

      // Send any still-local items and get the server's real amount due
      const freshCheck = await ensureItemsOnCheck(currentCheckId);
      const serverDue = freshCheck.amountDueCents ?? 0;

      // Get current split ID if this is a split payment
      const currentSplitId = splitConfig?.splitIds?.[splitConfig.currentSplit];

      if (paymentMethod === 'cash') {
        // Accept "20", "20.50" AND "20,50" (comma decimals) — parseFloat on a
        // comma-decimal quietly truncates, and unparseable input became NaN →
        // null on the wire → a raw "Validation failed" from the server.
        const parsedTendered = cashReceived
          ? parseFloat(cashReceived.replace(/,/g, '.').replace(/[^0-9.]/g, ''))
          : NaN;
        if (cashReceived && !Number.isFinite(parsedTendered)) {
          throw new Error('That cash amount doesn\'t look right — numbers only, like 20 or 20.50.');
        }
        const tenderedAmount = cashReceived
          ? Math.round(parsedTendered * 100)
          : Math.round(splitConfig ? total : serverDue + splitTipAmount);
        const response = await paymentsApi.processCash(currentCheckId, tenderedAmount, splitTipAmount, currentSplitId);
        if (!response.success) throw new Error(response.error?.message || 'Payment failed');
        return response.data;
      } else {
        const chargeAmount = Math.round(splitConfig ? total : serverDue + splitTipAmount);
        const response = await paymentsApi.createIntent(currentCheckId, chargeAmount, splitTipAmount, currentSplitId);
        if (!response.success) throw new Error(response.error?.message || 'Payment failed');
        return response.data;
      }
    },
    onSuccess: (data: any) => {
      if (splitConfig && splitConfig.currentSplit < splitConfig.splits - 1) {
        const nextSplit = splitConfig.currentSplit + 1;
        setSplitConfig({
          ...splitConfig,
          currentSplit: nextSplit,
          paidSplits: [...splitConfig.paidSplits, splitConfig.currentSplit],
        });
        setCashReceived('');
        setTipPercent(18);
        setCustomTip('');
        setSuccess(true);
        setTimeout(() => setSuccess(false), 1500);
      } else {
        setSuccess(true);
        showSuccess(
          data?.offline
            ? 'Cash payment saved — it will sync as soon as you’re back online.'
            : 'Payment successful!'
        );
        setTimeout(() => {
          clearOrder();
          // Navigate to feedback page instead of tables
          if (checkId) {
            navigate(`/feedback/${checkId}`);
          } else {
            navigate('/tables');
          }
        }, 1500);
      }
    },
    onError: (error: Error) => {
      console.error('Payment failed:', error);
      showError(
        error.message || 'Payment failed. Please try again.',
        {
          label: 'Retry',
          onClick: () => processPaymentMutation.mutate(),
        }
      );
    },
  });

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const handlePrintReceipt = async () => {
    try {
      await printerService.printReceipt({
        restaurantName: restaurant?.name || 'Restaurant',
        restaurantAddress: restaurant?.address,
        restaurantPhone: restaurant?.phone,
        checkNumber: checkId || 'N/A',
        tableNumber: tableNumber?.toString(),
        guestName: guestName || undefined,
        serverName: employee ? `${employee.firstName} ${employee.lastName}` : undefined,
        items: items.map((item) => ({
          name: item.shortName || item.name,
          quantity: item.quantity,
          priceCents: item.priceCents,
          modifiers: item.modifiers,
        })),
        subtotalCents: subtotal,
        taxCents: tax,
        tipCents: tipAmount,
        discountCents: discountAmount,
        discountReason: discount?.reason,
        totalCents: total,
        paymentMethod: paymentMethod,
        amountTenderedCents: paymentMethod === 'cash' && cashReceived ? Math.round(parseFloat(cashReceived) * 100) : undefined,
        changeCents: paymentMethod === 'cash' && cashChange > 0 ? cashChange : undefined,
        printedAt: new Date(),
      });
    } catch (error) {
      console.error('Print error:', error);
      showError('Failed to print receipt. Check printer connection.');
    }
  };

  const handleProcessPayment = async () => {
    // For card payments, we need a checkId to show the modal
    if (paymentMethod === 'card' && isOnline) {
      let currentCheckId = checkId;

      // Create check if it doesn't exist
      if (!currentCheckId && tableId) {
        try {
          const checkResponse = await checksApi.open({
            tableId: tableId,
            partySize: partySize || 1,
          });
          if (checkResponse.success && checkResponse.data?.id) {
            currentCheckId = checkResponse.data.id;
            useOrderStore.getState().setCheckId(currentCheckId);
          }
        } catch (error) {
          console.error('Failed to create check:', error);
          showError('Failed to create check. Please try again.');
          return;
        }
      }

      if (currentCheckId) {
        // Anything still local must be on the check before the card is
        // charged — otherwise the charge amount is wrong or rejected
        try {
          await ensureItemsOnCheck(currentCheckId);
        } catch (e: any) {
          showError(e?.message || "Couldn't prepare the check for payment.");
          return;
        }
        // Tap to Pay on iPhone is the card flow on device; the keyed modal
        // stays as the web / fallback path (reader unavailable, tap declined).
        if (isTapToPaySupported()) {
          runTapToPay(currentCheckId);
        } else {
          setShowCardPaymentModal(true);
        }
        return;
      }
    }
    processPaymentMutation.mutate();
  };

  const runTapToPay = async (currentCheckId: string) => {
    const splitTipAmount = splitConfig ? Math.round(tipAmount / splitConfig.splits) : tipAmount;
    const currentSplitId = splitConfig?.splitIds?.[splitConfig.currentSplit];
    const runId = ++tapRunIdRef.current;
    // Anything that resolves after the user hit Cancel belongs to a dead run
    const stillCurrent = () => tapRunIdRef.current === runId;
    try {
      // Charge exactly what the server says is due (plus tip) — never local
      // cart math, which drifts on discounts/voids/other devices
      const fresh = await checksApi.getOne(currentCheckId);
      const serverDue = fresh.success ? ((fresh.data as any)?.amountDueCents ?? 0) : 0;
      const chargeAmount = Math.round(splitConfig ? total : serverDue + splitTipAmount);
      if (chargeAmount <= 0) {
        setTapStatus(null);
        showError('This check has nothing due.');
        return;
      }
      const { paymentIntentId } = await takeTapToPayPayment({
        checkId: currentCheckId,
        amountCents: chargeAmount,
        tipAmountCents: splitTipAmount,
        splitCheckId: currentSplitId,
        merchantDisplayName: restaurant?.name,
        onStatus: (s) => {
          if (stillCurrent()) setTapStatus(s);
        },
      });
      if (!stillCurrent()) return;
      // Record the payment in Mise (updates the check, tips, table state)
      const confirmRes = await paymentsApi.confirmPayment(paymentIntentId);
      if (!stillCurrent()) return;
      setTapStatus(null);
      if (!confirmRes.success) {
        showError(confirmRes.error?.message || 'Payment went through but could not be recorded — check Recent Payments.');
        return;
      }
      handleCardPaymentSuccess(paymentIntentId);
    } catch (e: any) {
      if (!stillCurrent()) return; // user cancelled — stay quiet
      setTapStatus(null);
      showError(e?.message || "Tap to Pay didn't complete.", {
        label: 'Enter card manually',
        onClick: () => setShowCardPaymentModal(true),
      });
    }
  };

  const handleCardPaymentSuccess = (paymentId: string) => {
    setShowCardPaymentModal(false);

    if (splitConfig && splitConfig.currentSplit < splitConfig.splits - 1) {
      const nextSplit = splitConfig.currentSplit + 1;
      setSplitConfig({
        ...splitConfig,
        currentSplit: nextSplit,
        paidSplits: [...splitConfig.paidSplits, splitConfig.currentSplit],
      });
      setTipPercent(18);
      setCustomTip('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 1500);
    } else {
      setSuccess(true);
      setTimeout(() => {
        clearOrder();
        // Navigate to feedback page instead of tables
        if (checkId) {
          navigate(`/feedback/${checkId}`);
        } else {
          navigate('/tables');
        }
      }, 1500);
    }
  };

  const cashChange = cashReceived
    ? Math.round(parseFloat(cashReceived) * 100) - total
    : 0;

  if (success) {
    const isPartialSplit = splitConfig && splitConfig.currentSplit < splitConfig.splits - 1;
    return (
      <div className="h-full flex items-center justify-center bg-emerald-500">
        <div className="text-center text-white px-6">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-10 h-10 text-emerald-500" />
          </div>
          <h2 className="text-xl font-bold mb-1">
            {isPartialSplit ? 'Split Paid!' : 'Payment Complete!'}
          </h2>
          <p className="text-emerald-100 text-lg">
            {formatCurrency(total)}
          </p>
          {isPartialSplit && (
            <p className="text-emerald-200 mt-2 text-sm">
              Next: Split {(splitConfig?.currentSplit || 0) + 2} of {splitConfig?.splits}
            </p>
          )}
          {!isPartialSplit && (
            <button
              onClick={handlePrintReceipt}
              className="mt-6 flex items-center gap-2 mx-auto bg-white/20 hover:bg-white/30 px-5 py-2.5 rounded-lg transition-colors"
            >
              <Printer className="w-5 h-5" />
              Print Receipt
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-3 py-2.5 flex items-center gap-2">
        <button
          onClick={() => navigate(`/order/${tableId}`)}
          className="p-1.5 -ml-1 rounded-lg hover:bg-slate-100"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h2 className="font-semibold">Checkout</h2>
          <p className="text-xs text-slate-500">Table {tableNumber}</p>
        </div>
        <button
          onClick={handlePrintReceipt}
          className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"
        >
          <Printer className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scroll-bounce">
        {/* Order Summary - Collapsible */}
        <div className="bg-white mx-3 mt-3 rounded-xl shadow-sm overflow-hidden">
          <button
            onClick={() => setShowOrderDetails(!showOrderDetails)}
            className="w-full px-4 py-3 flex items-center justify-between"
          >
            <div>
              <span className="font-semibold">Order Summary</span>
              <span className="text-slate-500 text-sm ml-2">
                ({items.reduce((sum, i) => sum + i.quantity, 0)} items)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold">{formatCurrency(subtotal)}</span>
              {showOrderDetails ? (
                <ChevronUp className="w-5 h-5 text-slate-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              )}
            </div>
          </button>

          {showOrderDetails && (
            <div className="px-4 pb-3 border-t border-slate-100">
              <div className="space-y-2 py-3">
                {items.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-slate-600">
                      {item.quantity}x {item.shortName || item.name}
                    </span>
                    <span>{formatCurrency(item.priceCents * item.quantity)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Payment Method */}
        <div className="bg-white mx-3 mt-3 rounded-xl shadow-sm p-4">
          <h3 className="font-semibold mb-3 text-sm">Payment Method</h3>
          <div className="grid grid-cols-2 gap-2">
            {[
              // On iPhone the card flow IS Tap to Pay on iPhone. Apple reqs
              // 5.4/5.5: exact feature name on the button + the contactless
              // symbol (wave.3.right.circle, drawn as SVG for the webview).
              { id: 'card', label: isTapToPaySupported() ? 'Tap to Pay on iPhone' : 'Card', icon: isTapToPaySupported() ? ContactlessIcon : CreditCard },
              { id: 'cash', label: 'Cash', icon: Banknote },
              { id: 'apple_pay', label: 'Apple Pay', icon: Smartphone },
              { id: 'gift_card', label: 'Gift Card', icon: Gift },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setPaymentMethod(id as PaymentMethod)}
                className={clsx(
                  'py-3 px-3 rounded-xl flex items-center gap-2 transition-colors',
                  // Apple req 5.2: Tap to Pay on iPhone is unambiguously at
                  // the TOP of the list — full-width above the other options
                  id === 'card' && isTapToPaySupported() && 'col-span-2 justify-center',
                  paymentMethod === id
                    ? 'bg-primary-100 text-primary-700 border-2 border-primary-300'
                    : 'bg-slate-100 text-slate-600 border-2 border-transparent'
                )}
              >
                {Icon && <Icon className="w-5 h-5" />}
                <span className="font-medium text-sm">{label}</span>
              </button>
            ))}
          </div>

          {/* Cash Calculator */}
          {paymentMethod === 'cash' && (
            <div className="mt-3 p-3 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <Calculator className="w-4 h-4 text-slate-500" />
                <span className="font-medium text-sm">Cash Received</span>
              </div>
              <div className="relative mb-2">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                <input
                  type="number"
                  placeholder="0.00"
                  value={cashReceived}
                  onChange={(e) => setCashReceived(e.target.value)}
                  className="w-full py-3 pl-7 pr-3 bg-white rounded-lg text-xl font-bold focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              {cashReceived && (
                <div
                  className={clsx(
                    'text-center py-2 rounded-lg font-semibold',
                    cashChange >= 0
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-red-100 text-red-700'
                  )}
                >
                  {cashChange >= 0 ? `Change: ${formatCurrency(cashChange)}` : `Short: ${formatCurrency(Math.abs(cashChange))}`}
                </div>
              )}
              <div className="grid grid-cols-4 gap-2 mt-2">
                {[20, 50, 100].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setCashReceived(amount.toString())}
                    className="py-2 bg-white rounded-lg border border-slate-200 text-sm font-medium"
                  >
                    ${amount}
                  </button>
                ))}
                <button
                  onClick={() => setCashReceived((total / 100).toFixed(2))}
                  className="py-2 bg-white rounded-lg border border-slate-200 text-sm font-medium"
                >
                  Exact
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Split & Discount */}
        <div className="flex gap-2 px-3 mt-3">
          <button
            onClick={() => setShowSplitModal(true)}
            className={clsx(
              'flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-medium transition-colors',
              splitConfig
                ? 'bg-primary-100 text-primary-700 border-2 border-primary-300'
                : 'bg-white text-slate-600 border border-slate-200'
            )}
          >
            <Users className="w-4 h-4" />
            {splitConfig ? `${splitConfig.currentSplit + 1}/${splitConfig.splits}` : 'Split'}
          </button>
          <button
            onClick={() => setShowDiscountModal(true)}
            className={clsx(
              'flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-medium transition-colors',
              discount
                ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-300'
                : 'bg-white text-slate-600 border border-slate-200'
            )}
          >
            <Percent className="w-4 h-4" />
            {discount ? `${discount.type === 'percent' ? `${discount.value}%` : formatCurrency(discount.value)}` : 'Discount'}
          </button>
        </div>

        {/* Tip Selection */}
        <div className="bg-white mx-3 mt-3 rounded-xl shadow-sm p-4">
          <h3 className="font-semibold mb-3 text-sm">Add Tip</h3>
          <div className="grid grid-cols-5 gap-2">
            <button
              onClick={() => {
                setTipPercent(null);
                setCustomTip('');
              }}
              className={clsx(
                'py-2.5 rounded-lg text-sm font-medium transition-colors',
                !tipPercent && !customTip
                  ? 'bg-slate-800 text-white'
                  : 'bg-slate-100 text-slate-600'
              )}
            >
              None
            </button>
            {tipPresets.map((percent) => (
              <button
                key={percent}
                onClick={() => {
                  setTipPercent(percent);
                  setCustomTip('');
                }}
                className={clsx(
                  'py-2.5 rounded-lg text-sm font-medium transition-colors',
                  tipPercent === percent && !customTip
                    ? 'bg-primary-600 text-white'
                    : 'bg-slate-100 text-slate-600'
                )}
              >
                {percent}%
              </button>
            ))}
          </div>
          <div className="mt-2 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
            <input
              type="number"
              placeholder="Custom tip"
              value={customTip}
              onChange={(e) => {
                setCustomTip(e.target.value);
                setTipPercent(null);
              }}
              className="w-full py-2.5 pl-7 pr-3 bg-slate-100 rounded-lg text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Spacer for fixed bottom */}
        <div className="h-4" />
      </div>

      {/* Bottom - Totals & Pay Button */}
      <div className="bg-white border-t border-slate-200">
        {/* Totals */}
        <div className="px-4 py-3 space-y-1 text-sm">
          <div className="flex justify-between text-slate-600">
            <span>Subtotal</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
          {discountAmount > 0 && (
            <div className="flex justify-between text-emerald-600">
              <span>Discount</span>
              <span>-{formatCurrency(discountAmount)}</span>
            </div>
          )}
          <div className="flex justify-between text-slate-600">
            <span>Tax</span>
            <span>{formatCurrency(tax)}</span>
          </div>
          <div className="flex justify-between text-slate-600">
            <span>Tip</span>
            <span>{formatCurrency(tipAmount)}</span>
          </div>
          {splitConfig && (
            <div className="flex justify-between text-primary-600 font-medium">
              <span>Split {splitConfig.currentSplit + 1}/{splitConfig.splits}</span>
              <span>{formatCurrency(getCurrentSplitAmount())}</span>
            </div>
          )}
        </div>

        {/* Pay Button */}
        <div className="px-4 pb-4">
          <button
            onClick={handleProcessPayment}
            disabled={
              processPaymentMutation.isPending ||
              (paymentMethod === 'cash' && cashChange < 0)
            }
            className="w-full bg-emerald-600 text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 active:bg-emerald-700 disabled:opacity-50 disabled:active:bg-emerald-600"
          >
            {processPaymentMutation.isPending ? (
              'Processing...'
            ) : (
              <>
                <Check className="w-5 h-5" />
                Pay {formatCurrency(total)}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Modals */}
      {showSplitModal && (
        <SplitCheckModal
          items={items}
          subtotal={discountedSubtotal}
          tax={tax}
          total={fullTotal}
          partySize={partySize}
          onConfirm={async (config) => {
            // Call backend to create split records and get IDs
            if (checkId) {
              try {
                const response = await checksApi.split(checkId, {
                  splitType: config.type,
                  numberOfWays: config.splits,
                  splits: config.type === 'custom' && config.amounts
                    ? config.amounts.map((amount, i) => ({ amount, guestName: `Guest ${i + 1}` }))
                    : undefined,
                });
                if (response.success && response.data) {
                  setSplitConfig({
                    ...config,
                    splitIds: response.data.splits.map((s: any) => s.id),
                    currentSplit: 0,
                    paidSplits: [],
                  });
                }
              } catch (error) {
                console.error('Failed to split check:', error);
                // Fall back to local-only split (without backend tracking)
                setSplitConfig({
                  ...config,
                  currentSplit: 0,
                  paidSplits: [],
                });
              }
            } else {
              // No check ID yet, just set local config
              setSplitConfig({
                ...config,
                currentSplit: 0,
                paidSplits: [],
              });
            }
            setShowSplitModal(false);
          }}
          onCancel={() => setShowSplitModal(false)}
        />
      )}

      {showDiscountModal && (
        <DiscountModal
          subtotal={subtotal}
          onApply={(newDiscount) => {
            applyDiscount(newDiscount);
            setShowDiscountModal(false);
          }}
          onCancel={() => setShowDiscountModal(false)}
        />
      )}

      {/* Manager PIN for discounts that need approval */}
      {discountPinPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-1">Manager approval needed</h3>
            <p className="text-slate-500 text-sm mb-4">
              This discount needs a manager — enter a manager PIN to apply it.
            </p>
            <input
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={discountPin}
              onChange={(e) => setDiscountPin(e.target.value)}
              placeholder="Manager PIN"
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-center text-xl tracking-widest mb-4 focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setDiscountPinPrompt(null);
                  setDiscountPin('');
                }}
                className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={() => applyDiscount(discountPinPrompt, discountPin)}
                disabled={discountPin.length < 4}
                className="flex-1 py-3 bg-primary-600 text-white rounded-xl font-semibold disabled:opacity-50"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tap to Pay overlay — calm, one message at a time */}
      {tapStatus && (
        <div className="fixed inset-0 z-50 bg-primary-900/95 flex flex-col items-center justify-center p-8 text-center safe-top safe-bottom">
          <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center mb-6">
            <Smartphone className="w-10 h-10 text-white" />
          </div>
          <p className="text-2xl font-semibold text-white mb-2">
            {/* During 'present', Apple's system Tap to Pay sheet is on screen
                and provides the actual "hold your card" instruction — the app
                must NOT draw its own competing tap instruction (Apple review
                requirement), so we defer to the system prompt here. */}
            {tapStatus === 'present'
              ? 'Follow the prompt on screen'
              : tapStatus === 'processing'
              ? 'Processing…'
              : tapStatus === 'ready'
              ? 'Starting payment…'
              : 'Getting the reader ready…'}
          </p>
          <p className="text-primary-200 text-lg mb-8">
            {`$${(total / 100).toFixed(2)}`}
          </p>
          {/* Cancel is always available — a stuck "getting ready" state must
              never trap the screen (only hidden mid-charge). Dismiss FIRST:
              the SDK cancel can hang, and the user must never wait on it. */}
          {tapStatus !== 'processing' && (
            <button
              onClick={() => {
                tapRunIdRef.current++; // invalidate the in-flight run
                setTapStatus(null);
                cancelTapToPay().catch(() => {});
              }}
              className="px-6 py-3 rounded-xl bg-white/10 text-white font-medium"
            >
              Cancel
            </button>
          )}
        </div>
      )}

      {showCardPaymentModal && checkId && (
        <CardPaymentModal
          checkId={checkId}
          amount={total}
          tipAmount={tipAmount}
          onSuccess={handleCardPaymentSuccess}
          onCancel={() => setShowCardPaymentModal(false)}
        />
      )}
    </div>
  );
}
